import { CSSObject, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { ifNotProp, ifProp } from 'styled-tools';
import Checkmark from '~/icons/checkmark.svg';
import Eye from '~/icons/eye.svg';
import EyeClose from '~/icons/eye-close.svg';
import Spinner from '~/icons/spinner.svg';

type StyledInputProps = {
    withLabel: boolean;
    hideField?: boolean;
    isValid?: boolean;
};

type StyledLabelProps = {
    hasFocus?: boolean;
    isActive?: boolean;
    isValid?: boolean;
    required?: boolean;
};

type StyledInputFieldWrapperProps = {
    isValid: boolean;
};

export const inputRequiredStyle: CSSObject = {
    '::after': {
        content: "' *'",
    },
};

export const StyledInputWrapper = styled.div({});

export const StyledInput = styled.input<StyledInputProps>(
    ({ theme }) => ({
        ...theme.mixins.useTextStyle('formInput'),
        borderRadius: theme.sizes.cornerRadius,
        color: theme.colors.black,
        border: 0,
        lineHeight: theme.lineHeights.single,
        position: 'relative',
        cursor: 'type',
        outline: 0,
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'left',
        display: 'block',
        padding: `${theme.space[3]}`,
        backgroundColor: 'transparent',
        '&::placeholder': {
            transition: '0.1s color',
            color: theme.colors.grey45,
        },
        '&:empty:placeholder-shown::placeholder': {
            color: 'transparent',
        },
        '&:focus:empty::placeholder': {
            color: theme.colors.grey45,
        },
        '&:not(:placeholder-shown) ~ label, &:focus ~ label': {
            backgroundColor: theme.colors.white,
            paddingLeft: theme.space[1],
            paddingRight: theme.space[1],
            transform: 'translateY(-30px)',
            ...theme.mixins.useTextStyle('formsLabelActive'),
        },
        '::-webkit-calendar-picker-indicator:focus-visible': {
            boxShadow: theme.shadows.accessibility,
        },
    }),
    ifProp({ withLabel: false }, ({ theme }) => ({
        [`&:empty:placeholder-shown::placeholder`]: {
            color: theme.colors.grey45,
        },
    })),
    ifProp('hideField', () => ({
        height: 0,
        opacity: 0,
        padding: 0,
        border: 0,
        margin: 0,
    })),
);

export const StyledLabel = styled.label<StyledLabelProps>(
    ({ theme }) => ({
        position: 'absolute',
        top: '50%',
        left: theme.space[3],
        ...theme.mixins.useTextStyle('formsLabel'),
        transition: '0.1s transform, 0.1s width',
        color: theme.colors.black,
        transform: 'translateY(-50%)',
        maxWidth: `calc(100% - ${theme.space[3]} - ${theme.space[3]})`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transformOrigin: 'left top',
        pointerEvents: 'none',
    }),
    ifNotProp('isValid', ({ theme }) => ({
        color: theme.colors.negative,
    })),
    ifProp('required', { ...inputRequiredStyle }),
);

export const StyledInputFieldWrapper = styled.div<StyledInputFieldWrapperProps>(
    ({ theme, isValid }) => ({
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'auto min-content',
        borderRadius: theme.sizes.cornerRadius,
        alignItems: 'center',

        border: `1px solid ${!isValid ? theme.colors.negative : theme.colors.grey50}`,
        backgroundColor: 'transparent',
        [`&:active ${StyledLabel}`]: {
            color: isValid ? theme.colors.black : theme.colors.negative,
        },
        ':has(:focus-visible)': {
            boxShadow: theme.shadows.accessibilityTextField,
        },
    }),
);

export const StyledAdditionalContent = styled.span(() => ({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    svg: {
        overflow: 'visible',
    },
}));

export const StyledWrapper = styled.div(() => ({
    position: 'relative',
    flexGrow: 1,
}));

export const InputCheckmark = styled(Checkmark)(({ theme }) => ({
    width: '18px',
    height: '18px',
    marginRight: 'calc(' + theme.space[3] + ' - ' + theme.space[1] + ')',
    marginTop: '2px',
}));

export const InputViewPassword = styled(Eye)(({ theme }) => ({
    width: '18px',
    height: '18px',
    marginRight: 'calc(' + theme.space[3] + ' - ' + theme.space[1] + ')',
    marginTop: '2px',
    cursor: 'pointer',
    ':focus-visible': {
        boxShadow: theme.shadows.accessibility,
    },
}));

export const InputHidePassword = styled(EyeClose)(({ theme }) => ({
    width: '18px',
    height: '18px',
    marginRight: 'calc(' + theme.space[3] + ' - ' + theme.space[1] + ')',
    marginTop: '2px',
    cursor: 'pointer',
    ':focus-visible': {
        boxShadow: theme.shadows.accessibility,
    },
}));

const ROTATING_ANIMATION = keyframes({
    to: { transform: 'rotate(360deg);' },
});

export const InputSpinner = styled(Spinner)(({ theme }) => ({
    width: '18px',
    height: '18px',
    marginRight: 'calc(' + theme.space[3] + ' - ' + theme.space[1] + ')',
    marginTop: '2px',
    animation: `${ROTATING_ANIMATION} 1.5s infinite`,
}));
